import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faCalendar, faCalendarCheck, faClose } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import {
	useResetCustomEventMutation,
	useGetCustomEventsQuery,
} from "@/pages/Private/redux/customEvent/customEvent.api";

import { Icon, LoadingOverlay, Modal } from "@/components";
import { useTableSearch } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

import { Table, TableColumn } from "@/components/Table/Table";
import { useBulkFunctions } from "@/utils/userBulkFunctions";

import { JobsScrapeStatus } from "@/enum/scrape-enum";
import { AutoCompleteItem as DropDownItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { classNames } from "@/utils/classNames";

import { CustomEvent } from "../schema/customEvent";
import { AssignFilesModal } from "./AssignFilesModal";
import { ScrapeRequestQueueCell } from "./ScrapeRequestQueueCell";

export const CustomEventTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`customEvent.${key}`), [t]);
	const [selectedCustomEvent, setSelectedCustomEvent] = useState<CustomEvent | null>(null);
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query } = useTableSearch();
	const [showReassignmentModal, setShowReassignmentModal] = useState<boolean>(false);
	const [showClearModal, setShowClearModal] = useState<boolean>(false);
	const [resetCustomEvent] = useResetCustomEventMutation();
	const [confirmReset, setConfirmReset] = useState<string>("");

	const [status, setStatus] = useState<DropDownItem | undefined>(undefined);
	const { selectAll, toggleSelectAll } = useBulkFunctions();

	const { data, isLoading, isFetching } = useGetCustomEventsQuery({
		page,
		limit,
		searchValue: query,
	});

	const handleFileSelection = (customEvent: CustomEvent) => {
		setSelectedCustomEvent(customEvent);
		setShowReassignmentModal(true);
	};

	const handleFilesAssigned = () => {
		setSelectedCustomEvent(null);
		selectAll && toggleSelectAll();
		setShowReassignmentModal(false);
	};

	const handleClearFiles = (customEvent: CustomEvent) => {
		setSelectedCustomEvent(customEvent);
		setShowClearModal(true);
	};

	const tableColumns: TableColumn[] = useMemo(() => {
		return [
			{
				title: ts("status"),
				width: "fit-content",
				allowExclude: false,
				uid: "status",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => (
					<div>
						{row?.status === JobsScrapeStatus.DRAFT && (
							<Icon className="text-status-info" icon={faCalendar} />
						)}
						{row?.status === JobsScrapeStatus.DONE && (
							<Icon className="text-status-success" icon={faCalendarCheck} />
						)}
					</div>
				),
			},
			{
				title: ts("name"),
				width: "fit-content",
				allowExclude: false,
				uid: "name",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => <div>{row?.name}</div>,
			},
			{
				title: ts("file"),
				width: "fit-content",
				allowExclude: false,
				uid: "file",
				minWidth: "150px",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => (
					<div>
						<div
							className={classNames(
								"!py-2 !h-[34px] items-center group text-sm pl-2 bg-brand-white border border-gray-200 rounded-xl  cursor-pointer flex justify-between",
								row?.csvEnrichments?.length > 0 ? "text-status-info " : "text-blue-action"
							)}
							onClick={() =>
								row?.csvEnrichments?.length === 0 ? handleFileSelection(row) : handleClearFiles(row)
							}
						>
							{row.csvEnrichments?.length === 0 ? "Select file" : row.csvEnrichments?.length}
							{row.csvEnrichments?.length > 0 && (
								<Icon
									className="ml-2 text-status-error hidden group-hover:inline-flex mr-2"
									icon={faClose}
								/>
							)}
						</div>
					</div>
				),
			},
			{
				title: ts("company"),
				width: "fit-content",
				allowExclude: false,
				uid: "company",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => <div>{row?.company?.name}</div>,
			},
			{
				title: ts("entityType"),
				width: "fit-content",
				allowExclude: false,
				uid: "entityType",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => <div>{row?.lists?.[0]?.entityType}</div>,
			},
			{
				title: ts("listName"),
				width: "fit-content",
				allowExclude: false,
				uid: "listName",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => (
					<div>
						{row?.lists?.[0]?.name} ({row?.lists?.[0]?.id})
					</div>
				),
			},
			{
				title: ts("listProvidedUntil"),
				width: "fit-content",
				allowExclude: false,
				uid: "listProvidedUntil",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => (
					<div>
						{row?.listProvidedUntil && format(new Date(row?.listProvidedUntil), "dd.MM.yyyy")}
					</div>
				),
			},
			{
				title: ts("rescrape.admin.openRequest"),
				width: "fit-content",
				allowExclude: false,
				uid: "scrapeRequests",
				hideable: true,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => <ScrapeRequestQueueCell customEvent={row} />,
			},
			{
				title: ts("listProvidedAt"),
				width: "fit-content",
				allowExclude: false,
				uid: "listProvidedAt",
				hideable: false,
				moveable: true,
				show: true,
				render: (row: CustomEvent) => (
					<div>{row?.listProvidedAt && format(new Date(row?.listProvidedAt), "dd.MM.yyyy")}</div>
				),
			},
		];
	}, [ts]);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleCloseClearModal = () => {
		setShowClearModal(false);
		setConfirmReset("");
	};

	const handleResetCustomEvent = async () => {
		try {
			await resetCustomEvent(selectedCustomEvent?.id ?? 0).unwrap();
			setConfirmReset("");
			setShowClearModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	return !data ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="flex items-end align-baseline justify-between  mb-8">
				<div className="flex items-end align-baseline justify-between w-1/2">
					<div className="flex-grow mr-4 ">
						<InputField
							handleChange={(e: ChangeEvent<HTMLInputElement>) => {
								setSearchValue(e.target.value);
							}}
							iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
							name={"search"}
							placeholder={ts("search")}
							value={searchValue}
						/>
					</div>
				</div>
				<div className="flex items-center justify-end gap-2 mb-3">
					<Dropdown
						data={[
							{ title: "All", id: "" },
							{ title: "Done", id: JobsScrapeStatus.DONE },
							{ title: "Open", id: JobsScrapeStatus.DRAFT },
						]}
						defaultValue={{ title: "All", id: "" }}
						floating={true}
						handleSelect={(value?: DropDownItem) => {
							setStatus(value);
						}}
						title={ts("status")}
						value={{
							title: `${status?.title || ""} `,
							id: status?.title || ("" as string),
						}}
					/>
				</div>
			</div>
			<div className="relative w-full pr-0">
				<div className="pr-4 pb-[20px]">
					<Table
						columns={tableColumns}
						data={data.data || []}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						handleFetchPage={(page) => {
							if (selectAll) {
								toggleSelectAll();
							}

							setPage(page);
						}}
						hidePagination={false}
						isLoading={isFetching}
						limit={limit}
						overallCount={data.meta?.itemCount}
						page={page}
						sticky={false}
						totalPages={data.meta?.pageCount}
					/>
				</div>
			</div>
			{showClearModal && (
				<Modal
					disableSave={selectedCustomEvent?.name !== confirmReset}
					handleClose={handleCloseClearModal}
					handleSave={handleResetCustomEvent}
					isLoading={isLoading}
					isOpened={showClearModal}
					submitButtonColor="error"
					submitButtonText={t("basics.reset")}
					title={ts("reset.title")}
				>
					<div className="text-sm mb-4">
						Do you want to reset the following custom event? This will remove all assigned files.
					</div>

					<InputField isDisabled={true} name={"name"} value={selectedCustomEvent?.name || ""} />
					<div className="text-sm mb-4">
						To confirm the reset, please type the name of the custom event.
					</div>
					<InputField
						handleChange={(e) => setConfirmReset(e.target.value)}
						name="confirm"
						value={confirmReset}
					/>
				</Modal>
			)}
			{showReassignmentModal && (
				<AssignFilesModal
					customEvent={selectedCustomEvent}
					handleAssigned={handleFilesAssigned}
					handleClose={() => setShowReassignmentModal(false)}
				/>
			)}
		</div>
	);
};
